// ─────────────────────────────────────────────────────────────────────────
// components/UploadZone.jsx
// Reliable file picker: native <label> + full-size transparent <input>
// (programmatic input.click() is flaky on some Windows/Chrome setups).
// ─────────────────────────────────────────────────────────────────────────

import React, { useState, useId } from "react";
import { Box, Typography, Chip } from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { C } from "../constants/colors";

// Broad accept — MIME + extensions so Windows file dialogs don't grey out docs
const DEFAULT_ACCEPT = [
  ".pdf", ".txt", ".md", ".doc", ".docx", ".csv", ".xlsx", ".xls",
  ".png", ".jpg", ".jpeg", ".webp", ".gif", ".bmp",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "text/csv",
  "image/*",
].join(",");

const DEFAULT_HINT = "PDF, DOCX, TXT, CSV, XLSX, images";

export const UploadZone = ({
  paramKey,
  files,
  setFiles,
  accept = DEFAULT_ACCEPT,
  hint = DEFAULT_HINT,
  compact = false,
  label = null,
}) => {
  const [drag, setDrag] = useState(false);
  const inputId = useId();

  const add = (fileList) => {
    if (!setFiles || !fileList || !fileList.length) return;
    const incoming = Array.from(fileList).filter(Boolean);
    if (!incoming.length) return;
    setFiles((prev) => {
      const base = prev && typeof prev === "object" ? prev : {};
      const existing = Array.isArray(base[paramKey]) ? base[paramKey] : [];
      return { ...base, [paramKey]: [...existing, ...incoming] };
    });
  };

  const remove = (i) => {
    if (!setFiles) return;
    setFiles((prev) => {
      const base = prev && typeof prev === "object" ? prev : {};
      const existing = Array.isArray(base[paramKey]) ? base[paramKey] : [];
      return { ...base, [paramKey]: existing.filter((_, j) => j !== i) };
    });
  };

  const list = (files && files[paramKey]) || [];

  return (
    <Box sx={{ mt: compact ? 0.75 : 1.25 }}>
      {label && (
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: C.muted, mb: 0.5 }}>
          {label}
        </Typography>
      )}

      <Box
        component="label"
        htmlFor={inputId}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDrag(false);
          add(e.dataTransfer.files);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDrag(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setDrag(false);
        }}
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          border: `1px dashed ${drag ? C.primary : C.borderStrong}`,
          borderRadius: "8px",
          px: compact ? 1.5 : 2,
          py: compact ? 1.25 : 1.5,
          cursor: "pointer",
          background: drag ? C.primarySoft : C.bg,
          transition: "all .15s",
          overflow: "hidden",
          "&:hover": {
            borderColor: C.primary,
            background: C.primarySoft,
          },
        }}
      >
        {/* Native file input covers the whole zone — click always opens dialog */}
        <Box
          component="input"
          id={inputId}
          type="file"
          multiple
          accept={accept}
          onChange={(e) => {
            add(e.target.files);
            e.target.value = "";
          }}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            opacity: 0,
            cursor: "pointer",
            zIndex: 2,
            fontSize: 0,
          }}
        />

        <CloudUploadOutlinedIcon
          sx={{ fontSize: compact ? 18 : 22, color: drag ? C.primary : C.muted, flexShrink: 0 }}
        />
        <Box sx={{ flex: 1, minWidth: 0, pointerEvents: "none" }}>
          <Typography sx={{ fontSize: compact ? 12 : 13, color: C.muted, lineHeight: 1.4 }}>
            Drop files here or{" "}
            <Box component="span" sx={{ color: C.primary, fontWeight: 700 }}>
              browse
            </Box>
          </Typography>
          <Typography sx={{ fontSize: 11, color: C.faint, mt: 0.25 }}>
            {hint}
          </Typography>
        </Box>
      </Box>

      {list.length > 0 && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.75, mt: 1 }}>
          {list.map((f, i) => (
            <Chip
              key={`${f.name}-${i}-${f.size || 0}`}
              icon={<InsertDriveFileOutlinedIcon sx={{ fontSize: 14 }} />}
              label={`${f.name}${f.size ? ` (${Math.max(1, Math.round(f.size / 1024))} KB)` : ""}`}
              onDelete={() => remove(i)}
              deleteIcon={<CloseIcon sx={{ fontSize: 13 }} />}
              size="small"
              sx={{
                background: C.tagBg,
                color: C.tagText,
                fontSize: 12,
                fontWeight: 500,
                maxWidth: "100%",
                "& .MuiChip-label": { overflow: "hidden", textOverflow: "ellipsis" },
              }}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
